export const campaignStatuses = ["active", "paused", "retired"] as const;
export type CampaignStatus = (typeof campaignStatuses)[number];

export const serviceIds = ["bokeping", "tax", "payroll", "partner-service", "other-service"] as const;
export type ServiceId = (typeof serviceIds)[number];

export const preferredTimeValues = [
  "Morning", "Afternoon", "Evening",
  "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM",
] as const;

export const contactMethods = ["Email", "Phone", "Text"] as const;
export type ContactMethod = (typeof contactMethods)[number];

export type Campaign = {
  id: string;
  slug: string;
  partnerId: string;
  organizationId: string;
  partnerDisplayName: string;
  areaCode: string;
  keyword: string;
  headline: string;
  introduction: string;
  partnerProfile: {
    fullName: string;
    professionalRole: string;
    companyName: string;
    companyDescription: string;
    serviceArea?: string;
    profileImage?: string;
    companyLogo?: string;
    primaryServiceName: string;
    primaryServiceDescription: string;
  };
  services: ServiceId[];
  status: CampaignStatus;
  fallbackPath: string;
  theme: "doors" | "tax";
  assets: Array<{ id: string; label: string; kind: "flyer" | "contact-card"; href: string; approved: boolean }>;
};

export type Attribution = {
  partnerId: string;
  organizationId: string;
  campaignId: string;
  ambassadorId?: string;
  printBatchId?: string;
  assetId?: string;
  channel?: string;
  landingRoute: string;
  visitId: string;
  firstVisitAt: string;
};

export type LeadSubmission = {
  idempotencyKey: string;
  campaignSlug: string;
  name: string;
  email: string;
  phone: string;
  requestedService: ServiceId;
  requestedServices: ServiceId[];
  preferredContactMethod?: ContactMethod;
  preferredDate?: string;
  preferredTime?: string;
  message?: string;
  consent: { accepted: true; version: "2026-09-04"; statement: string; capturedAt: string };
  attribution: Attribution;
  website?: string;
  formStartedAt: string;
};

export const partnerStatuses = ["recommended", "under-review", "approved", "active", "paused", "declined", "terminated", "archived"] as const;
export type PartnerStatus = (typeof partnerStatuses)[number];
export type PartnerKind = "primary" | "distribution" | "service-provider";

export type Partner = {
  id: string;
  displayName: string;
  legalName?: string;
  kind: PartnerKind;
  status: PartnerStatus;
  email: string;
  phone?: string;
  territory?: string;
  referredByPartnerId?: string;
  relationshipOwner: string;
  services: ServiceId[];
  createdAt: string;
};

export const recommendationStatuses = ["submitted", "under-review", "more-information-needed", "approved", "declined"] as const;
export type RecommendationStatus = (typeof recommendationStatuses)[number];
export type PartnerRecommendation = {
  id: string;
  recommendedName: string;
  email: string;
  phone?: string;
  territory?: string;
  relationship?: string;
  reason: string;
  recommendedByPartnerId: string;
  status: RecommendationStatus;
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
};

export const leadStatuses = ["new", "reviewing", "qualified", "assigned", "contact-attempted", "connected", "service-accepted", "not-qualified", "unable-to-reach", "declined", "closed", "duplicate"] as const;
export type LeadStatus = (typeof leadStatuses)[number];

export type LeadRecord = {
  leadId: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  location?: string;
  requestedService: ServiceId;
  requestedServices?: ServiceId[];
  preferredContactMethod?: ContactMethod;
  preferredDate?: string;
  preferredTime?: string;
  message?: string;
  status: LeadStatus;
  campaignId: string;
  distributionPartnerId: string;
  originalReferringPartnerId?: string;
  serviceProviderId?: string;
  consentCapturedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type AuditEvent = {
  id: string;
  actorId: string;
  action: string;
  entityType: "partner" | "recommendation" | "campaign" | "lead" | "asset";
  entityId: string;
  occurredAt: string;
  reason?: string;
};
